import { useState } from 'react';
import type { ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAudit, fetchConnections, fetchHealth, testConnection } from '../api/endpoints';
import { errorMessageRu } from '../api/errors';
import { queryKeys } from '../api/query-keys';
import type { AuditEntry, Connection, HealthReady } from '../api/schemas';
import { useSession } from '../auth/session-context';
import { withReauth } from '../auth/with-reauth';
import { Banner } from '../components/Banner';
import { EmptyState } from '../components/EmptyState';
import { Field } from '../components/Field';
import { KeyValue } from '../components/KeyValue';
import { QueryState } from '../components/QueryState';
import { Section } from '../components/Section';
import { StatusPill } from '../components/StatusPill';
import { TableScroll } from '../components/TableScroll';
import {
  AUDIT_SOURCE_LABEL,
  auditActionRu,
  CONNECTION_KIND_LABEL,
  CONNECTION_STATUS_LABEL,
  CONNECTION_STATUS_TONE,
  WARNING_LEVEL_LABEL,
  WARNING_LEVEL_TONE,
} from '../domain/labels';
import { formatDateTime, isoDateDaysAgo, todayIsoDate } from '../lib/datetime';
import { newIdempotencyKey } from '../lib/idempotency';

/**
 * Раздел 7 — «Аудит и подключения».
 *
 * The audit log is append-only and this screen honours that literally: it
 * renders rows and nothing else. There is no edit control, no delete control
 * and no "исправить" affordance anywhere in this file — the only interactive
 * elements are the date filter and the connection check.
 *
 * API keys are never displayed. The connections list shows a fingerprint, which
 * is enough to tell two keys apart and useless to anyone who steals it.
 */
export function AuditPage(): ReactElement {
  const [from, setFrom] = useState(() => isoDateDaysAgo(14));
  const [to, setTo] = useState(() => todayIsoDate());

  const filter = {
    ...(from === '' ? {} : { from }),
    ...(to === '' ? {} : { to }),
  };

  const auditQuery = useQuery({
    queryKey: queryKeys.audit(filter),
    queryFn: ({ signal }) => fetchAudit(filter, signal),
  });
  const connectionsQuery = useQuery({
    queryKey: queryKeys.connections,
    queryFn: ({ signal }) => fetchConnections(signal),
  });
  const healthQuery = useQuery({
    queryKey: queryKeys.health,
    queryFn: ({ signal }) => fetchHealth(signal),
  });

  return (
    <>
      <div className="page-head">
        <h1>Аудит и подключения</h1>
        <span className="page-head__hint">
          Журнал действий доступен только для чтения. Ключи площадки показаны отпечатком.
        </span>
      </div>

      <div className="stack">
        <Section title="Подключения" hint="Проверка выполняется на сервере и пишется в аудит.">
          <QueryState query={connectionsQuery} what="список подключений">
            {(data) =>
              data.items.length === 0 ? (
                <EmptyState>Подключений не настроено.</EmptyState>
              ) : (
                <ConnectionsList connections={data.items} />
              )
            }
          </QueryState>
        </Section>

        <Section title="Состояние сервисов">
          <QueryState query={healthQuery} what="состояние сервисов">
            {(health) => <HealthBlock health={health} />}
          </QueryState>
        </Section>

        <Section
          title="Журнал аудита"
          hint="Записи неизменяемы: журнал только пополняется и никогда не редактируется."
        >
          <div className="stack stack--tight">
            <div className="filters">
              <Field id="audit-from" label="Дата с">
                <input
                  id="audit-from"
                  type="date"
                  value={from}
                  onChange={(event) => {
                    setFrom(event.target.value);
                  }}
                />
              </Field>
              <Field id="audit-to" label="Дата по">
                <input
                  id="audit-to"
                  type="date"
                  value={to}
                  onChange={(event) => {
                    setTo(event.target.value);
                  }}
                />
              </Field>
            </div>

            <QueryState query={auditQuery} what="журнал аудита">
              {(data) =>
                data.items.length === 0 ? (
                  <EmptyState>За выбранный период записей нет.</EmptyState>
                ) : (
                  <AuditTable entries={data.items} />
                )
              }
            </QueryState>
          </div>
        </Section>
      </div>
    </>
  );
}

function ConnectionsList({ connections }: { connections: readonly Connection[] }): ReactElement {
  const queryClient = useQueryClient();
  const { canOperate, promptReauth } = useSession();
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [errorRu, setErrorRu] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (id: string) =>
      withReauth(() => testConnection(id, newIdempotencyKey()), promptReauth),
    onSuccess: async () => {
      setErrorRu(null);
      setCheckingId(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.connections });
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
    onError: (error: unknown) => {
      setCheckingId(null);
      setErrorRu(errorMessageRu(error));
    },
  });

  return (
    <div className="stack stack--tight">
      {errorRu !== null && (
        <Banner tone="danger" title="Проверка не выполнена.">
          {errorRu}
        </Banner>
      )}

      <ul className="stack stack--tight">
        {connections.map((connection) => (
          <li key={connection.id} className="stack stack--tight">
            <div className="row">
              <strong>{connection.name}</strong>
              <StatusPill tone="neutral" srPrefix="Тип подключения">
                {CONNECTION_KIND_LABEL[connection.kind]}
              </StatusPill>
              <StatusPill
                tone={CONNECTION_STATUS_TONE[connection.status]}
                srPrefix="Состояние подключения"
              >
                {CONNECTION_STATUS_LABEL[connection.status]}
              </StatusPill>
              <button
                type="button"
                className="btn"
                disabled={!canOperate || mutation.isPending}
                onClick={() => {
                  setCheckingId(connection.id);
                  mutation.mutate(connection.id);
                }}
              >
                {mutation.isPending && checkingId === connection.id ? 'Проверка…' : 'Проверить'}
              </button>
            </div>
            <KeyValue
              rows={[
                { label: 'Подробности', value: connection.detailsRu },
                { label: 'Последняя проверка', value: formatDateTime(connection.lastCheckedAt) },
                {
                  label: 'Отпечаток ключа',
                  value:
                    connection.keyFingerprint === null ? (
                      <span className="muted">ключ не используется</span>
                    ) : (
                      <span className="mono">{connection.keyFingerprint}</span>
                    ),
                },
              ]}
            />
          </li>
        ))}
      </ul>

      <p className="field__hint">
        Сам ключ не хранится в браузере и не передаётся в интерфейс — показывается только отпечаток.
      </p>
    </div>
  );
}

function HealthBlock({ health }: { health: HealthReady }): ReactElement {
  return (
    <div className="stack stack--tight">
      <div className="row">
        <StatusPill tone={health.ready ? 'ok' : 'danger'} srPrefix="Готовность сервиса">
          {health.ready ? 'Сервис готов' : 'Сервис не готов'}
        </StatusPill>
      </div>

      <KeyValue
        rows={[
          { label: 'Версия', value: health.version },
          { label: 'Время сервера', value: formatDateTime(health.serverTime) },
          { label: 'Время площадки', value: formatDateTime(health.venueTime) },
          {
            label: 'Расхождение часов',
            value:
              health.clockSkewMs === null ? (
                <span className="muted">—</span>
              ) : (
                `${String(health.clockSkewMs)} мс`
              ),
          },
          { label: 'Задач в очереди', value: String(health.queue.pending) },
          { label: 'Задач с ошибкой', value: String(health.queue.failed) },
        ]}
      />

      {health.incidents.length === 0 ? (
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          Открытых инцидентов нет.
        </p>
      ) : (
        <ul className="stack stack--tight">
          {health.incidents.map((incident) => (
            <li key={incident.id} className="row">
              <StatusPill tone={WARNING_LEVEL_TONE[incident.level]} srPrefix="Уровень инцидента">
                {WARNING_LEVEL_LABEL[incident.level]}
              </StatusPill>
              <span className="reason-text">{incident.messageRu}</span>
              <span className="muted" style={{ fontSize: '0.78rem' }}>
                {formatDateTime(incident.at)}
                {incident.resolvedAt !== null && ` · закрыт ${formatDateTime(incident.resolvedAt)}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AuditTable({ entries }: { entries: readonly AuditEntry[] }): ReactElement {
  return (
    <TableScroll label="Журнал аудита, прокручивается по горизонтали">
      <table className="data-table">
        <caption>Записи журнала аудита за выбранный период (только чтение)</caption>
        <thead>
          <tr>
            <th scope="col">Время</th>
            <th scope="col">Автор</th>
            <th scope="col">Источник</th>
            <th scope="col">Действие</th>
            <th scope="col" className="wrap">
              Подробности
            </th>
            <th scope="col">Ключ идемпотентности</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id}>
              <td>{formatDateTime(entry.at)}</td>
              <td>{entry.actorEmail ?? 'система'}</td>
              <td>{AUDIT_SOURCE_LABEL[entry.source]}</td>
              <td>{auditActionRu(entry.action)}</td>
              <td className="wrap">{entry.detailsRu}</td>
              <td className="mono">{entry.idempotencyKey ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}
